// Agency Clients - Agency-wide client list with agent breakdown
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Users, Search, Filter, RefreshCw, ArrowLeft,
  ChevronRight, Phone, Mail, Clock, AlertTriangle,
  Download, Upload, X, UserCircle, Building2, ChevronDown
} from 'lucide-react';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// Risk level configurations
const RISK_LEVELS = {
  critical: { label: 'Critical', color: 'bg-red-600', textColor: 'text-red-600', bgLight: 'bg-red-50', min: 85 },
  high: { label: 'High', color: 'bg-orange-500', textColor: 'text-orange-600', bgLight: 'bg-orange-50', min: 65 },
  elevated: { label: 'Elevated', color: 'bg-yellow-500', textColor: 'text-yellow-600', bgLight: 'bg-yellow-50', min: 50 },
  moderate: { label: 'Moderate', color: 'bg-blue-500', textColor: 'text-blue-600', bgLight: 'bg-blue-50', min: 35 },
  low: { label: 'Low', color: 'bg-green-400', textColor: 'text-green-600', bgLight: 'bg-green-50', min: 20 },
  stable: { label: 'Stable', color: 'bg-green-600', textColor: 'text-green-700', bgLight: 'bg-green-50', min: 0 }
};

function getRiskLevel(score) {
  if (score >= 85) return 'critical';
  if (score >= 65) return 'high';
  if (score >= 50) return 'elevated';
  if (score >= 35) return 'moderate';
  if (score >= 20) return 'low';
  return 'stable';
}

// Client Row Component
function ClientRow({ client, onViewClient }) {
  const riskLevel = getRiskLevel(client.riskScore || 0);
  const config = RISK_LEVELS[riskLevel];

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${config.color} flex items-center justify-center text-white font-bold text-sm`}>
            {client.riskScore || 0}
          </div>
          <div>
            <div className="font-medium text-gray-900">
              {client.firstName} {client.lastName}
            </div>
            <div className="text-sm text-gray-500">
              {client.carrier || 'No carrier'}
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <UserCircle size={16} className="text-gray-400" />
          <span className="text-sm text-gray-700">{client.agentName || 'Unassigned'}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bgLight} ${config.textColor}`}>
          {config.label}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-500">
        {client.topRiskFactor || 'None identified'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500">
        {client.lastContactDate
          ? new Date(client.lastContactDate).toLocaleDateString()
          : 'Never'
        }
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {client.phone && (
            <a
              href={`tel:${client.phone}`}
              onClick={e => e.stopPropagation()}
              className="p-2 hover:bg-blue-100 rounded-lg text-blue-600"
              title="Call client"
            >
              <Phone size={16} />
            </a>
          )}
          {client.email && (
            <a
              href={`mailto:${client.email}`}
              onClick={e => e.stopPropagation()}
              className="p-2 hover:bg-blue-100 rounded-lg text-blue-600"
              title="Email client"
            >
              <Mail size={16} />
            </a>
          )}
          <Link
            to={`/clients/${client.id}`}
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
            title="View profile"
          >
            <ChevronRight size={16} />
          </Link>
        </div>
      </td>
    </tr>
  );
}

// Main Agency Clients Page
export default function AgencyClients() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });

  // Filters from URL
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [agentFilter, setAgentFilter] = useState(searchParams.get('agentId') || 'all');
  const [riskFilter, setRiskFilter] = useState(searchParams.get('riskLevel') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'risk');

  // Load agents for filter dropdown
  useEffect(() => {
    async function loadAgents() {
      try {
        const res = await fetch(`${BASE}/agency/agents`, { credentials: 'include' });
        const data = await res.json();
        if (data.ok) {
          setAgents(data.agents);
        }
      } catch (err) {
        console.error('Failed to load agents:', err);
      }
    }
    loadAgents();
  }, []);

  // Load clients
  useEffect(() => {
    async function loadClients() {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: pagination.page.toString(),
          limit: pagination.limit.toString(),
          sortBy
        });

        if (searchQuery) params.set('search', searchQuery);
        if (agentFilter !== 'all') params.set('agentId', agentFilter);
        if (riskFilter !== 'all') params.set('riskLevel', riskFilter);

        const res = await fetch(`${BASE}/agency/clients?${params}`, { credentials: 'include' });
        if (res.status === 403) {
          navigate('/dashboard');
          return;
        }
        const data = await res.json();
        if (data.ok) {
          setClients(data.clients);
          setPagination(prev => ({
            ...prev,
            total: data.total || data.clients.length,
            totalPages: data.totalPages || Math.ceil((data.total || data.clients.length) / prev.limit)
          }));
        }
      } catch (err) {
        console.error('Failed to load clients:', err);
      } finally {
        setLoading(false);
      }
    }
    loadClients();
  }, [navigate, pagination.page, pagination.limit, sortBy, agentFilter, riskFilter, searchQuery]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (agentFilter !== 'all') params.set('agentId', agentFilter);
    if (riskFilter !== 'all') params.set('riskLevel', riskFilter);
    if (sortBy !== 'risk') params.set('sortBy', sortBy);
    setSearchParams(params);
  }, [searchQuery, agentFilter, riskFilter, sortBy, setSearchParams]);

  // Handle search with debounce
  const [searchInput, setSearchInput] = useState(searchQuery);
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Calculate stats
  const riskCounts = {
    critical: clients.filter(c => c.riskScore >= 85).length,
    high: clients.filter(c => c.riskScore >= 65 && c.riskScore < 85).length,
    elevated: clients.filter(c => c.riskScore >= 50 && c.riskScore < 65).length,
    atRisk: clients.filter(c => c.riskScore >= 50).length
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (agentFilter !== 'all') params.set('agentId', agentFilter);
      if (riskFilter !== 'all') params.set('riskLevel', riskFilter);

      const res = await fetch(`${BASE}/agency/clients/export?${params}`, {
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Export failed');

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `agency-clients-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-4 sm:py-6 lg:py-8 px-3 sm:px-4 lg:px-6">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <Link
              to="/agency/dashboard"
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2"
            >
              <ArrowLeft size={16} />
              Back to Dashboard
            </Link>
            <div className="flex items-center gap-3">
              <Users size={28} className="text-[#172A3A]" />
              <h1 className="text-2xl sm:text-3xl font-bold text-[#172A3A]">
                All Clients
              </h1>
            </div>
            <p className="text-gray-600 mt-1">
              View and manage all clients across your agency
            </p>
          </div>

          <div className="flex items-center gap-2 self-start">
            <button
              onClick={() => navigate('/clients/import')}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Upload size={18} className="text-gray-600" />
              Import
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download size={18} className="text-gray-600" />
              Export
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="text-3xl font-bold text-[#172A3A]">{pagination.total}</div>
            <div className="text-sm text-gray-500">Total Clients</div>
          </div>
          <div className="bg-red-50 rounded-xl p-4 shadow-sm border border-red-100">
            <div className="text-3xl font-bold text-red-600">{riskCounts.critical}</div>
            <div className="text-sm text-red-600">Critical Risk</div>
          </div>
          <div className="bg-orange-50 rounded-xl p-4 shadow-sm border border-orange-100">
            <div className="text-3xl font-bold text-orange-600">{riskCounts.high}</div>
            <div className="text-sm text-orange-600">High Risk</div>
          </div>
          <div className="bg-yellow-50 rounded-xl p-4 shadow-sm border border-yellow-100">
            <div className="text-3xl font-bold text-yellow-600">{riskCounts.elevated}</div>
            <div className="text-sm text-yellow-600">Elevated Risk</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search clients by name, phone, or carrier..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchInput && (
              <button
                onClick={() => { setSearchInput(''); setSearchQuery(''); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            )}
          </div>

          {/* Filter Group */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter size={18} className="text-gray-400" />

              {/* Agent Filter */}
              <select
                value={agentFilter}
                onChange={(e) => { setAgentFilter(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
                className="border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Agents</option>
                {agents.map(agent => (
                  <option key={agent.userId} value={agent.userId}>
                    {agent.name}
                  </option>
                ))}
              </select>

              {/* Risk Filter */}
              <select
                value={riskFilter}
                onChange={(e) => { setRiskFilter(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
                className="border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Risk Levels</option>
                <option value="critical">Critical (85+)</option>
                <option value="high">High (65-84)</option>
                <option value="elevated">Elevated (50-64)</option>
                <option value="moderate">Moderate (35-49)</option>
                <option value="low">Low (&lt;35)</option>
              </select>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="risk">Highest Risk</option>
                <option value="name">Name A-Z</option>
                <option value="recent">Recently Added</option>
                <option value="lastContact">Last Contact</option>
              </select>
            </div>
          </div>
        </div>

        {/* Client Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw size={24} className="animate-spin mx-auto mb-2 text-gray-400" />
              <p className="text-gray-500">Loading clients...</p>
            </div>
          ) : clients.length === 0 ? (
            <div className="p-8 text-center">
              <Users size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No clients found</h3>
              <p className="text-gray-500">
                {searchQuery || agentFilter !== 'all' || riskFilter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'No clients in your agency yet'
                }
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Agent
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Risk Level
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Top Risk Factor
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Last Contact
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {clients.map(client => (
                      <ClientRow key={client.id} client={client} />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} clients
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page === 1}
                      className="px-3 py-1 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page >= pagination.totalPages}
                      className="px-3 py-1 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
